import { ERROR_HANDLER_SET } from './modules/actions';
import { handledErrors } from 'errors/errors';
import ERRORS from '../errors/errorids';
import AppError from '../errors/apperror';
import Logger from '../logger/logger';
import _ from 'lodash';
import errorMessages from '../errors/errormessages';
const log = Logger.get('asyncMiddleware');
import {
	AUTHENTICATION,
	CRS,
	COUPONS,
	CONTACT_SALES,
	MLS_DATA_PROJECT_ENDPOINTS,
	ACCOUNT_UPDATE_EXTERNAL_IDS_ENDPOINT
} from '../config';
import {
	CRS_CHECK_CREDENTIALS,
	CRS_USAGE_UPDATE,
	COMPANIES_USER_ADD,
	COMPANIES_ESTIMATE_USER_ADD,
	COMPANIES_CREATE_CHARGEBEE_SUBSCRIPTION,
	COMPANIES_ESTIMATE_SEAT,
	COMPANIES_PURCHASE_SEAT,
	COUPON_FETCH_FAIL,
	COMPANIES_USERS_REDUCE,
	COMPANIES_CONTACT_SALES,
	COMPANIES_CONNECT_MLS_REQUEST,
	COMPANIES_REVIEW_MLS_REQUEST,
	SUBSCRIPTION_GET_ESTIMATE_FAIL,
	COMPANIES_CREATE_CHARGEBEE_SUBSCRIPTION_FAIL,
	USER_REGION_FETCH
} from './modules/actions';

export default function asyncMiddleware(apiClient, axiosClient) {
	return ({ dispatch, getState }) => {
		return next => action => {
			if (typeof action === 'function') {
				return action(dispatch, getState);
			}

			const { promise, types, ...rest } = action; // eslint-disable-line no-redeclare
			if (!promise) {
				return next(action);
			}

			// todo catch infinite loop here
			const [REQUEST, SUCCESS, FAILURE] = types;
			try {
				next({ ...rest, type: REQUEST });
				
				const client = REQUEST === CRS_CHECK_CREDENTIALS ||
					REQUEST === CRS_USAGE_UPDATE ||
					REQUEST === COMPANIES_CONTACT_SALES ||
					REQUEST === COMPANIES_REVIEW_MLS_REQUEST ||
					REQUEST === COMPANIES_CONNECT_MLS_REQUEST
					?
					axiosClient
					:
					apiClient;

				return promise(client)
					.then(result => next({ ...rest, result, type: SUCCESS }))
					.catch((error, body, xhr) => {
						const state = getState();

						let firstMessageId;
						let messageFromMw;
						if (error instanceof AppError) {
							// Extract message from MW response to be used in modal popup
							const firstMessage = error.getMesageFromHistoryAtIndex(0);

							if (firstMessage) {
								firstMessageId = firstMessage.errorId;
								
								try {
									const responseJSON = JSON.parse(_.get(firstMessage, 'responseText'));
									messageFromMw = responseJSON.errormessage;
								} catch (e) {
									// Silent catch
								}
							}
						}

						const errorIdToLog = firstMessageId || ERRORS.UNKNOWN;

						const errorProps = {
							message: 'Redux Error (promise-catch)',
							errorId: errorIdToLog,
							originalError: error,
							keepOriginalError: true,
							visualisers: {
								feature: errorIdToLog === ERRORS.USER_AUTHENTICATION_REQUIRED ||
									REQUEST === USER_REGION_FETCH && firstMessageId === ERRORS.COUNTRY_CANNOT_BE_DETECTED
									?
									'SUPPRESSED_ERROR' : 'REDUX'
							}
						};

						const responseURL = _.get(error, 'originalErrors[0].responseURL');

						if (FAILURE === COUPON_FETCH_FAIL || 
							((FAILURE === SUBSCRIPTION_GET_ESTIMATE_FAIL || 
								FAILURE === COMPANIES_CREATE_CHARGEBEE_SUBSCRIPTION_FAIL) && 
								(responseURL.indexOf('couponId') !== -1 || responseURL.indexOf('couponSetId') !== -1)
							)) {
							errorProps.details = {};
							errorProps.details.couponId = _.get(rest, 'payload.couponId') || _.get(rest, 'payload.couponSetId');
							errorProps.visualisers.feature = 'COUPON';
						}

						const nError = new AppError(errorProps);

						log.send(nError);

						if (!firstMessageId) {
							// Ask user to refresh app because of runtime erros (invalid Redux state)
							throw nError;
						}

						// Sets inline state for components (updates related redux module ie. COMPANIES:VALIDATE_FAIL)
						next({ ...rest, error: nError, result: {}, type: FAILURE });

						// - For API errors or known client side errors (OFFLINE, XHR_ABORTED) show popup
						// - Don't do anything for handledErrors like COMPANY_NAME_ALREADY_EXISTS, as they are
						//   handled in different way (COMPANY_NAME_ALREADY_EXISTS shows inline error
						//   above company name input field etc)
						// - Errors from CRS-WEB shows inline error in the discount form
						// - Errors from COMPANIES_USER_ADD && REQUEST and COMPANIES_ESTIMATE_USER_ADD actions
						// 	 are shown inline in Invite user popup
						// - Errors from COMPANIES_CREATE_CHARGEBEE_SUBSCRIPTION, COMPANIES_PURCHASE_SEAT and 
						//   COMPANIES_ESTIMATE_SEAT actions 	are shown inline in BuyMoreLicensesOrSeatsModal popup 
						//   if that modal is shown
						// - Errors from COMPANIES_USERS_REDUCE
						// - Errors from https://www.xara.com/contact-sales-request/ endpoint on website
						// - Errors from ACCOUNT_UPDATE_EXTERNAL_IDS_ENDPOINT endpoint

						const isBuyLicensesModalShown = state.modal.modalShown &&
							[
								'CONFIRM_PURCHASE_CHOICE',
								'BUY_MORE_LICENSES_OR_SEATS'
							].includes(state.modal.modalType);

						if (handledErrors.indexOf(firstMessageId) === -1 &&
							// All errors coming from CRS-WEB page are handled errors
							responseURL.indexOf(CRS.INFO_URL) === -1 &&
							// All errors coming from COUPONS.GET_COUPON_INFO route are handled errors
							responseURL.indexOf(COUPONS.GET_COUPON_INFO) === -1 &&

							// Errors during externalIds update are shown inline
							responseURL.indexOf(ACCOUNT_UPDATE_EXTERNAL_IDS_ENDPOINT) === -1 &&

							// Errors from subscription estimation endpoint when there is couponId or couponSetId sent
							(responseURL.indexOf('createSubscriptionEstimation') !== -1 && 
							(responseURL.indexOf('couponId') === -1 && responseURL.indexOf('couponSetId') === -1)) &&

							// All errors coming from CONTACT_SALES.API_ENDPOINT route are handled errors
							responseURL.indexOf(CONTACT_SALES.API_ENDPOINT) === -1 &&

							// All errors coming from CONNECT_MLS.API_ENDPOINT route are handled errors
							responseURL.indexOf(MLS_DATA_PROJECT_ENDPOINTS.CONNECT_MLS) === -1 &&
							responseURL.indexOf(MLS_DATA_PROJECT_ENDPOINTS.REVIEW_MLS) === -1 &&

							// All errors coming from 'Change sign-in details' page are handled errors
							responseURL.indexOf(AUTHENTICATION.SIGN_IN_URL) === -1 &&
							responseURL.indexOf(AUTHENTICATION.SIGN_UP_URL) === -1 &&
							responseURL.indexOf(AUTHENTICATION.RESET_PASSWORD_URL) === -1 &&
							(REQUEST !== COMPANIES_USER_ADD && REQUEST !== COMPANIES_ESTIMATE_USER_ADD) &&
							(REQUEST !== COMPANIES_CREATE_CHARGEBEE_SUBSCRIPTION || !isBuyLicensesModalShown) &&
							REQUEST !== COMPANIES_ESTIMATE_SEAT &&
							REQUEST !== COMPANIES_PURCHASE_SEAT &&
							REQUEST !== COMPANIES_USERS_REDUCE
						) {
							const isErrorMessageInConfig = errorMessages[firstMessageId];
							const reduxObject = {
								errorId: firstMessageId,
								...(!isErrorMessageInConfig && messageFromMw && { errorMessage: messageFromMw })
							};
							// Sets props for ErrorModal
							next({ type: ERROR_HANDLER_SET, error: reduxObject, result: {}});
						}
						return error;
					});
			} catch (error) {
				throw new AppError({
					message: 'Redux error (try-catch)',
					errorId: ERRORS.UNKNOWN,
					originalError: error,
					visualisers: { feature: 'REDUX' }
				});
			}
		};
	};
}


// WEBPACK FOOTER //
// ./src/redux/asyncMiddleware.js